/**
 * Color resolution for 領域 (area) chips. Delegates to the shared
 * 17-slot palette in `./colorPalette` so an area's color resolves
 * exactly like an assignee's — a `colorIndex` of N renders the same
 * Tailwind classes on both surfaces.
 *
 * Resolution priority (mirrors the assignee chip):
 *   1. The area's stored `colorIndex` (set when the user explicitly
 *      picked a color in the editor or when a freshly-added area got
 *      auto-assigned the next free slot).
 *   2. Deterministic name hash via `colorIndexFromName(name)` — used
 *      for legacy projects that still store `string[]` and for known
 *      `AREA_LABELS` whose color slots are stable but unsaved.
 *   3. UNASSIGNED_CLASSES (slate) for null/blank.
 *
 * The historical `areaBadgeClasses` API is preserved so call-sites
 * outside the editor (WBS tree row, area filter chip) keep working
 * without changes when no `colorIndex` lookup is available.
 */
import type { WbsCatalogOption } from '@mct/shared';
import {
  colorClassesByIndex,
  colorIndexFromName,
  UNASSIGNED_CLASSES,
} from './colorPalette';

export const AREA_LABELS = ['全体', '会計領域', 'サービス領域', '生産領域', '営業領域'] as const;

export type AreaLabel = typeof AREA_LABELS[number];

export function isKnownArea(value: string): value is AreaLabel {
  return (AREA_LABELS as readonly string[]).includes(value);
}

/**
 * Map of label -> stored `colorIndex`. Built once per render. Used by
 * 領域 / ベンダー / 担当 chip surfaces alike — all three share the same
 * shape now.
 */
export type AreaColorIndexMap = ReadonlyMap<string, number>;
/** Generic alias — the map shape is option-list agnostic. */
export type LabelColorIndexMap = AreaColorIndexMap;

export function buildLabelColorIndexMap(
  options: ReadonlyArray<WbsCatalogOption>,
): LabelColorIndexMap {
  const map = new Map<string, number>();
  for (const opt of options) {
    if (typeof opt.colorIndex === 'number' && Number.isFinite(opt.colorIndex)) {
      map.set(opt.label, opt.colorIndex);
    }
  }
  return map;
}

/** Backwards-compatible alias. */
export const buildAreaColorIndexMap = buildLabelColorIndexMap;

/**
 * Resolve palette classes for an area label, optionally consulting a
 * stored `colorIndex` map. When the map has an explicit entry we use
 * that slot; otherwise we fall back to the deterministic name hash so
 * legacy projects keep rendering predictably.
 */
export function resolveAreaColorClasses(
  area: string | null | undefined,
  colorIndices?: AreaColorIndexMap,
): string {
  if (!area) return UNASSIGNED_CLASSES;
  const explicit = colorIndices?.get(area);
  if (typeof explicit === 'number' && Number.isFinite(explicit)) {
    return colorClassesByIndex(explicit);
  }
  return colorClassesByIndex(colorIndexFromName(area));
}

/**
 * Filled pill for always-visible display in tree rows.
 *
 * Backwards-compatible signature: callers that don't have a colorIndex
 * map (e.g. legacy contexts) still get a sensible name-hashed color.
 */
export function areaBadgeClasses(
  area: string | null | undefined,
  colorIndices?: AreaColorIndexMap,
): string {
  if (!area) return 'bg-slate-100 text-slate-500 border border-slate-200';
  return resolveAreaColorClasses(area, colorIndices);
}

/**
 * Filter-chip variants. Selected chips use a darker variant of the
 * same hue so the active filter reads as the colour-coded "on" state.
 * Unselected chips stay neutral with a coloured dot (rendered via
 * `areaDotClass`).
 */
export function areaChipClasses(
  area: string,
  selected: boolean,
  colorIndices?: AreaColorIndexMap,
): string {
  if (!selected) {
    return 'bg-white text-slate-700 border-slate-200 hover:border-slate-400';
  }
  // Selected: pull a saturated counterpart by reusing the resolved
  // background but swapping the text color. We do this by hand here
  // (rather than building a parallel "solid" palette) because there
  // are only two states and the active chip just needs to read as
  // emphasised.
  const base = resolveAreaColorClasses(area, colorIndices);
  return `${base} ring-2 ring-offset-1 ring-slate-400 font-semibold`;
}

/**
 * Solid-dot color for compact swatches (filter chip prefix, color
 * picker grid). Returns a single Tailwind `bg-*` utility.
 */
export function areaDotClass(
  area: string | null | undefined,
  colorIndices?: AreaColorIndexMap,
): string {
  if (!area) return 'bg-slate-300';
  // Strip the `bg-*-100 text-*-800` pair down to its `bg-*` family by
  // re-extracting from the palette index. We re-resolve once so the dot
  // and the badge stay in lockstep.
  const classes = resolveAreaColorClasses(area, colorIndices);
  // classes look like "bg-red-100 text-red-800"; grab the first token.
  const first = classes.split(' ')[0];
  if (!first || !first.startsWith('bg-')) return 'bg-slate-300';
  // Map "bg-red-100" -> "bg-red-500" so the dot is more saturated than
  // the pill background.
  return first.replace(/-100$/, '-500');
}
